import { createNewDate } from '@mixins/moment-mixin';
import Order, { ORDER_TYPE_SERVICE } from '@models/order';
import Route, { ROUTE_TYPE_SERVICE } from '@models/route';
import Setting from '@models/setting';
import UserAvailability from '@models/user-availability';
import VirtualOrder from '@models/virtual-order';
import { debounce } from 'lodash';
import sortBy from 'lodash/sortBy';

const debouncedGetVirtualOrders = debounce(async ({ commit, state }) => {
    const virtualOrders = await VirtualOrder
        .when(state.startDate && state.endDate, (query) => query.where('date_range', { start: state.startDate.format('YYYY-MM-DD'), end: state.endDate.format('YYYY-MM-DD') }))
        .when(state.startDate && !state.endDate, (query) => query.where('date', state.startDate.format('YYYY-MM-DD')))
        .where('type', ROUTE_TYPE_SERVICE)
        .where('branch_id', state.filterByBranch)
        .with('customerLocation', 'customerLocation.customer')
        .limit(2000)
        .get();

    commit('setVirtualOrders', virtualOrders);
}, 700);

const debouncedGetUnassignedOrders = debounce(async ({ commit, state }) => {
    const orders = await Order.getUnassignedOrders(ORDER_TYPE_SERVICE, {
        start: state.startDate.format('YYYY-MM-DD'),
        end: state.endDate ? state.endDate.format('YYYY-MM-DD') : state.startDate.format('YYYY-MM-DD'),
    }, state.filterByBranch);

    commit('setUnassignedOrders', orders);
}, 700);

const debouncedGetRoutesByDate = debounce(async ({ commit, state }, { view }) => {
    commit('setLoadingRoutes', true);

    const routes = await Route
        .when(state.startDate && state.endDate, (query) => query.where('date_range', { start: state.startDate.format('YYYY-MM-DD'), end: state.endDate.format('YYYY-MM-DD') }))
        .when(state.startDate && !state.endDate, (query) => query.where('date', state.startDate.format('YYYY-MM-DD')))
        .where('type', ROUTE_TYPE_SERVICE)
        .where('branch_id', state.filterByBranch)
        .with('user', 'tasks', 'branch')
        .responseFormat('service-schedule-calendar')
        .when(view === 'day', (query) => query.query('include_polylines', true))
        .limit(2000)
        .get();

    commit('setRoutes', routes);
    commit('setLoadingRoutes', false);
}, 700);

const debouncedGetEventsByDate = debounce(async ({ commit, state }) => {
    const availabilities = await UserAvailability
        .where('date_range', {
            start: state.startDate.format('YYYY-MM-DD'),
            end: state.endDate ? state.endDate.format('YYYY-MM-DD') : state.startDate.format('YYYY-MM-DD'),
        })
        .where('branch_id', state.filterByBranch)
        .with('user')
        .limit(2000)
        .get();

    commit('setEvents', availabilities);
}, 700);

export default {
    namespaced: true,
    state: {
        startDate: createNewDate(),
        endDate: createNewDate().add(14, 'days'),
        routes: [],
        events: [],
        unassignedOrders: [],
        virtualOrders: [],
        selectedServiceTechnicians: {},
        filterByBranch: 1,
        config: {},
        loadingRoutes: true,
    },
    getters: {
        filteredRoutes(state) {
            let { routes } = state;

            if (state.filterByBranch) {
                routes = routes.filter((route) => route.starting_branch_id === state.filterByBranch);
            }

            routes = routes.filter((r) => state.selectedServiceTechnicians[r.user_id] || !r.user_id);

            return routes;
        },
        filteredEvents(state) {
            let { events } = state;

            if (state.filterByBranch) {
                events = events.filter((e) => e.user.branch_id === state.filterByBranch);
            }

            events = events.filter((e) => state.selectedServiceTechnicians[e.user_id]);

            return events;
        },
        filteredUnassignedOrders(state) {
            const { unassignedOrders } = state;

            let orders = unassignedOrders;
            if (state.filterByBranch) {
                orders = unassignedOrders.filter((o) => o.branch_id === state.filterByBranch);
            }

            return sortBy(orders, 'customer_location.name');
        },
        filteredVirtualOrders(state) {
            const { virtualOrders } = state;

            let orders = virtualOrders;
            if (state.filterByBranch) {
                orders = virtualOrders.filter((o) => o.branch_id === state.filterByBranch);
            }

            return sortBy(orders, 'customer_location.name');
        },
    },
    mutations: {
        config(state, config) {
            state.config = config;
        },
        setEvents(state, events) {
            state.events = events;
        },
        setRoutes(state, routes) {
            state.routes = routes;
        },
        setUnassignedOrders(state, unassignedOrders) {
            state.unassignedOrders = unassignedOrders;
        },
        setVirtualOrders(state, virtualOrders) {
            state.virtualOrders = virtualOrders;
        },
        setStartDate(state, date) {
            state.startDate = date;
        },
        setEndDate(state, date) {
            state.endDate = date;
        },
        setSelectedServiceTechnicians(state, technicians) {
            state.selectedServiceTechnicians = technicians;
        },
        setFilterBranch(state, branchId) {
            state.filterByBranch = branchId;
        },
        setRouteAsPendingOptimizing(state, { routeId, status = true }) {
            const routeIndex = state.routes.findIndex((r) => r.id === Number(routeId));

            if (routeIndex !== -1) {
                const updatedRoute = { ...state.routes[routeIndex], pending_optimizing: status };

                state.routes.splice(routeIndex, 1, new Route(updatedRoute));
            }
        },
        setRouteAsOptimizing(state, routeId) {
            const routeIndex = state.routes.findIndex((r) => r.id === Number(routeId));

            if (routeIndex !== -1) {
                const updatedRoute = { ...state.routes[routeIndex], optimizing: true };

                state.routes.splice(routeIndex, 1, new Route(updatedRoute));
            }
        },
        updateRoute(state, route) {
            const routeIndex = state.routes.findIndex((r) => r.id === route.id);

            const updatedRoute = new Route(route);

            if (routeIndex !== -1) {
                state.routes.splice(routeIndex, 1, updatedRoute);
            } else {
                state.routes.push(updatedRoute);
            }
        },
        removeRoute(state, routeId) {
            const routeIndex = state.routes.findIndex((r) => r.id === routeId);

            if (routeIndex !== -1) {
                state.routes.splice(routeIndex, 1);
            }
        },
        setLoadingRoutes(state, bool) {
            state.loadingRoutes = bool;
        },
    },
    actions: {
        async getConfiguration({ commit }) {
            const settings = await Setting.first();
            commit('config', settings);
        },
        getRoutesByDate({ commit, state }, { view }) {
            debouncedGetRoutesByDate({ commit, state }, { view });
        },
        getEventsByDate({ commit, state }) {
            debouncedGetEventsByDate({ commit, state });
        },
        getUnassignedOrders({ commit, state }) {
            debouncedGetUnassignedOrders({ commit, state });
        },
        getVirtualOrders({ commit, state }) {
            debouncedGetVirtualOrders({ commit, state });
        },
        filterByBranch({ commit }, branchId) {
            commit('setFilterBranch', branchId);
        },
        resetBranchFilter({ commit }) {
            commit('setFilterBranch', null);
        },
        async refresh({ dispatch }) {
            await Promise.all([
                dispatch('getRoutesByDate', { view: 'day' }),
                dispatch('getUnassignedOrders'),
                dispatch('getVirtualOrders'),
            ]);
        },
        async refreshRoute({ commit }, routeId) {
            const route = await Route
                .with('user', 'tasks')
                .find(routeId);

            commit('updateRoute', route);
        },
    },
};
